// =========================================================================
// RoutingScene — /issues/routing. Fork-owned (zone C).
//
// Every open alert and every flagged machine × component, classified as an
// operator, equipment or mixed problem and pushed to the person who acts on
// it — and, on the Training tab, the operators whose event rate against
// their site peers says they need coaching.
//
// Answers reviewer feedback (Paul Telford, 25 Sep 2026): "tracking shifts and
// who is driving could help identify who needs additional training … an
// ability to identify the root causes and push to the appropriate person
// would be ideal."
//
// UI layer. Engine calls run once over the session's reliability context
// (`useReliability`); filtering, KPIs, the training rows and the peer strips
// come from the sibling transform.
//
// @see docs/logic/issues/routing.md
// @see docs/engines/reliability.md
// =========================================================================

import { Fragment, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ChevronRight, GraduationCap } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  SkeletonBlock,
  SurfacePageBody,
  SurfacePageLayout,
  SurfacePageTabs,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { cn } from '@/shared/cn';
import type { RootCause } from '@/versions/v1/schema/operations';
import {
  operatorRates,
  rootCause,
  routingDecisions,
  trainingNeeds,
} from '@/versions/v1/shared/reliability';
import { useReliability } from '@/versions/v1/shared/reliability/useReliability';
import { useToast } from '@/versions/v1/shared/toast/ToastContext';
import { CausePill, RosterNote, RouteCards, useRouteSends } from './RouteCards';
import { CAUSE_META } from './RouteCards.transform';
import {
  ROLE_SHORT,
  ROUTING_TABS,
  STRIP_MAX_RATIO,
  causesIn,
  filterRows,
  minesIn,
  parseRoutingTab,
  routingKpis,
  routingRows,
  siteStrips,
  trainingRows,
  type CauseFilter,
  type MineFilter,
  type RoutingKpis,
  type RoutingRow,
  type RoutingTab,
  type SiteStrip,
  type TrainingRow,
} from './RoutingScene.transform';
import { ROLE_SIDE } from '@/versions/v1/shared/seed/site-roles';

const DESCRIPTION =
  'Each alert and flagged component is classified as how the machine is driven, the condition of the machine, or both — and pushed to the people who act on it.';

export function RoutingScene() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseRoutingTab(searchParams.get('tab'));
  const { ctx, loading, error } = useReliability();

  const data = useMemo(() => {
    if (!ctx) return null;
    const decisions = routingDecisions(ctx);
    const findings = new Map(
      decisions.map((d) => [`${d.machine}|${d.component}`, rootCause(d.machine, d.component, ctx)] as const),
    );
    const needs = trainingNeeds(ctx.attribution);
    return {
      rows: routingRows(decisions, findings),
      training: trainingRows(needs),
      strips: siteStrips(operatorRates(ctx.attribution), needs),
    };
  }, [ctx]);

  const setTab = (next: RoutingTab) => {
    setSearchParams(next === 'routing' ? {} : { tab: next }, { replace: false });
  };

  return (
    <SurfacePageLayout width="full">
      <Commentable name="RoutingScene">
        <div className="contents">
          <SurfacePageBody className="space-y-4" as="div">
            <Commentable name="RoutingHeader">
              <header className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h1 className="text-2xl font-semibold tracking-tight text-[var(--ink-primary)]">
                      Routing &amp; training{' '}
                      <span className="text-sm font-normal text-[var(--ink-tertiary)]">· who acts on what</span>
                    </h1>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[var(--ink-secondary)]">{DESCRIPTION}</p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    {data ? (
                      <span className="mono rounded-full bg-[var(--surface-2)] px-2.5 py-1 text-xs text-[var(--ink-secondary)]">
                        {data.rows.length} routed · {data.training.length} training needs
                      </span>
                    ) : null}
                    <HelpAnchor doc="issues/routing" />
                  </div>
                </div>
                <SurfacePageTabs
                  items={ROUTING_TABS.map((t) => ({
                    id: t.id,
                    label:
                      data === null
                        ? t.label
                        : `${t.label} ${t.id === 'routing' ? data.rows.length : data.training.length}`,
                  }))}
                  value={tab}
                  onChange={setTab}
                  aria-label="Routing view"
                />
              </header>
            </Commentable>

            <AskStrip view="routing" />

            {error ? (
              <EmptyState
                icon={<AlertTriangle className="h-8 w-8 text-[var(--crit)]" />}
                title="The shift and event data did not load"
                description={error.message}
              />
            ) : loading || !data ? (
              <div className="space-y-3" aria-busy>
                <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
                  {[0, 1, 2, 3].map((i) => (
                    <SkeletonBlock key={i} className="h-[92px] rounded-xl" />
                  ))}
                </div>
                <SkeletonBlock className="h-[420px] rounded-xl" />
              </div>
            ) : tab === 'routing' ? (
              <RoutingTabBody rows={data.rows} />
            ) : (
              <TrainingTabBody rows={data.training} strips={data.strips} />
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

// ---------------------------------------------------------------------------
// Routing tab
// ---------------------------------------------------------------------------

function RoutingTabBody({ rows }: { rows: readonly RoutingRow[] }) {
  const [cause, setCause] = useState<CauseFilter>('all');
  const [mine, setMine] = useState<MineFilter>('all');
  const [open, setOpen] = useState<string | null>(null);
  const sends = useRouteSends();
  const navigate = useNavigate();

  const kpis = useMemo(() => routingKpis(rows), [rows]);
  const shown = useMemo(() => filterRows(rows, cause, mine), [rows, cause, mine]);
  const mines = useMemo(() => minesIn(rows), [rows]);
  const causes = useMemo(() => causesIn(rows), [rows]);

  return (
    <>
      <KpiTiles kpis={kpis} cause={cause} onCause={setCause} />

      <Commentable name="RoutingTable">
        <section className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)]">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-[var(--hairline)] px-4 py-3">
            <FilterGroup
              label="Cause"
              value={cause}
              onChange={(v) => setCause(v as CauseFilter)}
              options={[
                { id: 'all', label: 'All' },
                ...causes.map((c) => ({ id: c, label: CAUSE_META[c].short, color: CAUSE_META[c].color })),
              ]}
            />
            <FilterGroup
              label="Site"
              value={mine}
              onChange={(v) => setMine(v as MineFilter)}
              options={[{ id: 'all', label: 'All' }, ...mines.map((m) => ({ id: m, label: m }))]}
            />
            <span className="ml-auto text-[11px] text-[var(--ink-tertiary)]">
              {shown.length} of {rows.length} · open alerts first
            </span>
          </div>

          {shown.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-[var(--ink-tertiary)]">
              Nothing routed matches those filters.
            </p>
          ) : (
            <div className="scrollbar-subtle overflow-x-auto">
              <table className="w-full min-w-[60rem] border-collapse text-[12px]">
                <thead>
                  <tr className="border-b border-[var(--hairline)] bg-[var(--surface-2)] text-left text-[10.5px] uppercase tracking-wide text-[var(--ink-tertiary)]">
                    <th className="w-8 px-2 py-2 font-medium">
                      <span className="sr-only">Expand</span>
                    </th>
                    <th className="px-2 py-2 font-medium">Machine · component</th>
                    <th className="px-2 py-2 font-medium">Cause</th>
                    <th className="px-2 py-2 font-medium">Source</th>
                    <th className="px-2 py-2 font-medium">Routed to</th>
                    <th className="px-2 py-2 font-medium">Driver</th>
                    <th className="px-3 py-2 text-right font-medium">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row) => {
                    const expanded = open === row.key;
                    return (
                      <Fragment key={row.key}>
                        <tr
                          onClick={() => navigate(row.href)}
                          className={cn(
                            'cursor-pointer border-b border-[var(--hairline)] transition-colors hover:bg-[var(--surface-2)]',
                            expanded && 'bg-[var(--surface-2)]',
                          )}
                        >
                          <td className="px-2 py-2 align-middle">
                            <button
                              type="button"
                              aria-expanded={expanded}
                              aria-label={`${expanded ? 'Hide' : 'Show'} routes for ${row.machine} ${row.component}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpen(expanded ? null : row.key);
                              }}
                              className="grid h-6 w-6 place-items-center rounded-md text-[var(--ink-tertiary)] hover:bg-[var(--surface-3)] hover:text-[var(--ink-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
                            >
                              <ChevronRight
                                aria-hidden
                                className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')}
                              />
                            </button>
                          </td>
                          <td className="px-2 py-2">
                            <Link
                              to={row.href}
                              onClick={(e) => e.stopPropagation()}
                              className="mono font-semibold text-[var(--ink-primary)] hover:text-[var(--accent)]"
                            >
                              {row.machine}
                            </Link>
                            <span className="ml-2 text-[var(--ink-secondary)]">{row.component}</span>
                            <span className="block text-[10.5px] text-[var(--ink-tertiary)]">{row.mineName}</span>
                          </td>
                          <td className="px-2 py-2">
                            <CausePill cause={row.cause} confidence={row.confidence} />
                          </td>
                          <td className="px-2 py-2">
                            {row.alertId ? (
                              <span className="mono whitespace-nowrap rounded bg-[var(--crit-soft)] px-1.5 py-0.5 text-[10.5px] font-medium text-[var(--crit)]">
                                {row.alertId}
                              </span>
                            ) : (
                              <span className="text-[11px] text-[var(--ink-tertiary)]">Flagged</span>
                            )}
                          </td>
                          <td className="px-2 py-2">
                            <span className="flex flex-wrap gap-1">
                              {row.roles.map((r) => (
                                <span
                                  key={r}
                                  className="rounded px-1.5 py-0.5 text-[10.5px] font-medium"
                                  style={
                                    ROLE_SIDE[r] === 'operations'
                                      ? { background: 'var(--info-soft)', color: 'var(--info)' }
                                      : { background: 'var(--warn-soft)', color: 'var(--warn)' }
                                  }
                                >
                                  {ROLE_SHORT[r]}
                                </span>
                              ))}
                            </span>
                          </td>
                          <td className="px-2 py-2">
                            {row.topOperator ? (
                              <span className="text-[11.5px]">
                                <span className="font-medium text-[var(--ink-primary)]">{row.topOperator.name}</span>
                                <span className="block text-[10.5px] text-[var(--ink-tertiary)]">
                                  crew {row.topOperator.crew} · {row.topOperator.shift === 'N' ? 'night' : 'day'} ·{' '}
                                  {row.topOperator.events} of {row.events} events
                                </span>
                              </span>
                            ) : (
                              <span className="text-[11px] text-[var(--ink-tertiary)]">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Link
                              to={row.href}
                              onClick={(e) => e.stopPropagation()}
                              className="whitespace-nowrap text-[11.5px] font-medium text-[var(--accent)] hover:underline"
                            >
                              {row.alertId ? 'alert' : 'machine'} ›
                            </Link>
                          </td>
                        </tr>
                        {expanded ? (
                          <tr className="border-b border-[var(--hairline)] bg-[var(--surface-2)]">
                            <td />
                            <td colSpan={6} className="px-2 pb-4 pt-1">
                              <ul className="mb-3 space-y-1">
                                {row.decision.evidence.slice(0, 3).map((line) => (
                                  <li key={line} className="flex gap-2 text-[11.5px] leading-relaxed text-[var(--ink-secondary)]">
                                    <span aria-hidden className="mt-[7px] inline-block h-1 w-1 flex-none rounded-full bg-[var(--ink-tertiary)]" />
                                    {line}
                                  </li>
                                ))}
                              </ul>
                              <RouteCards decision={row.decision} sends={sends} />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <RosterNote className="border-t border-[var(--hairline)] px-4 py-3" />
        </section>
      </Commentable>
    </>
  );
}

function KpiTiles({
  kpis,
  cause,
  onCause,
}: {
  kpis: RoutingKpis;
  cause: CauseFilter;
  onCause: (c: CauseFilter) => void;
}) {
  const tiles: { id: RootCause; detail: string }[] = [
    { id: 'operator', detail: 'how it is driven → supervisor, superintendent, mine control' },
    { id: 'equipment', detail: 'the component itself → reliability engineer, planner' },
    { id: 'mixed', detail: 'worn and driven hard → both sides act' },
  ];
  return (
    <Commentable name="RoutingKpis">
      <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
        {tiles.map((t) => {
          const meta = CAUSE_META[t.id];
          const active = cause === t.id;
          return (
            <button
              key={t.id}
              type="button"
              aria-pressed={active}
              onClick={() => onCause(active ? 'all' : t.id)}
              className={cn(
                'flex flex-col items-start justify-start rounded-xl border bg-[var(--surface-1)] p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]',
                active ? 'border-[var(--accent)]' : 'border-[var(--hairline)] hover:border-[var(--hairline-strong)]',
              )}
              style={{ borderLeft: `4px solid ${meta.color}` }}
            >
              <div className="mono text-[27px] font-semibold leading-none" style={{ color: meta.color }}>
                {kpis.byCause[t.id]}
              </div>
              <div className="mt-1 text-xs font-medium text-[var(--ink-primary)]">{meta.label}</div>
              <div className="mt-1 text-[10.5px] leading-relaxed text-[var(--ink-tertiary)]">
                {t.detail}
                {active ? ' · filtering' : ''}
              </div>
            </button>
          );
        })}
        <div className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4" style={{ borderLeft: '4px solid var(--accent)' }}>
          <div className="flex items-baseline gap-2">
            <span className="mono text-[27px] font-semibold leading-none" style={{ color: 'var(--info)' }}>
              {kpis.toOperations}
            </span>
            <span className="text-[11px] text-[var(--ink-tertiary)]">to operations</span>
            <span className="mono ml-2 text-[27px] font-semibold leading-none" style={{ color: 'var(--warn)' }}>
              {kpis.toMaintenance}
            </span>
            <span className="text-[11px] text-[var(--ink-tertiary)]">to maintenance</span>
          </div>
          <div className="mt-1 text-xs font-medium text-[var(--ink-primary)]">Where the {kpis.total} go</div>
          <div className="mt-1 text-[10.5px] leading-relaxed text-[var(--ink-tertiary)]">
            {kpis.alerts} open alerts and {kpis.total - kpis.alerts} flagged components · {kpis.people} named people
          </div>
        </div>
      </div>
    </Commentable>
  );
}

function FilterGroup({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly { id: string; label: string; color?: string }[];
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-center gap-1">
      <span className="mr-1 text-[10.5px] font-medium uppercase tracking-wide text-[var(--ink-tertiary)]">{label}</span>
      {options.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.id)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]',
              active
                ? 'border-[var(--ink-primary)] bg-[var(--ink-primary)] text-[var(--surface-1)]'
                : 'border-[var(--hairline)] text-[var(--ink-secondary)] hover:border-[var(--hairline-strong)]',
            )}
          >
            {o.color ? <i aria-hidden className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: o.color }} /> : null}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Training tab
// ---------------------------------------------------------------------------

function TrainingTabBody({ rows, strips }: { rows: readonly TrainingRow[]; strips: readonly SiteStrip[] }) {
  const { show } = useToast();
  const [booked, setBooked] = useState<ReadonlySet<string>>(() => new Set());

  const book = (row: TrainingRow) => {
    setBooked((prev) => new Set(prev).add(row.key));
    show(`Coaching booked for ${row.need.name} with ${row.supervisor} (Operating Supervisor, ${row.need.mine})`);
  };

  return (
    <>
      <div className="rounded-xl border border-dashed border-[var(--hairline-strong)] bg-[var(--surface-1)] px-4 py-3">
        <RosterNote />
        <p className="mt-1 text-[10.5px] leading-relaxed text-[var(--ink-tertiary)]">
          A training need is flagged when an operator runs at 1.8× or more of the rate of the other operators of the
          same class at the same site, on at least 20 hours and 5 events, and the excess is not chance (Poisson
          p ≤ 0.05). Events are the operator-sensitive alarm codes plus abuse.
        </p>
      </div>

      <Commentable name="PeerStrips">
        <section className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4">
          <div className="mb-2 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            <h2 className="text-sm font-semibold text-[var(--ink-primary)]">Every operator against their site peers</h2>
            <span className="text-[11.5px] text-[var(--ink-tertiary)]">
              each dot is one operator with 20 h or more · events per 100 h as a multiple of the same-class rate at
              the site
            </span>
          </div>
          <PeerStripChart strips={strips} />
        </section>
      </Commentable>

      <Commentable name="TrainingNeeds">
        <section className="space-y-2 pt-1">
          <div className="flex flex-wrap items-baseline gap-2.5">
            <h2 className="text-sm font-semibold text-[var(--ink-primary)]">Flagged for coaching</h2>
            <span className="text-[11.5px] text-[var(--ink-tertiary)]">
              {rows.length} operators · highest multiple first · goes to the site&rsquo;s operating supervisor
            </span>
          </div>
          {rows.length === 0 ? (
            <p className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] px-4 py-10 text-center text-sm text-[var(--ink-tertiary)]">
              No operator stands out from their site peers in this window.
            </p>
          ) : (
            <div className="divide-y divide-[var(--hairline)] rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)]">
              {rows.map((row) => (
                <TrainingNeedRow key={row.key} row={row} booked={booked.has(row.key)} onBook={() => book(row)} />
              ))}
            </div>
          )}
        </section>
      </Commentable>
    </>
  );
}

function TrainingNeedRow({ row, booked, onBook }: { row: TrainingRow; booked: boolean; onBook: () => void }) {
  const n = row.need;
  return (
    <article className="grid gap-3 p-4 lg:grid-cols-[minmax(0,13rem)_minmax(0,15rem)_minmax(0,1fr)_auto] lg:items-start">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <b className="text-[13px] font-semibold text-[var(--ink-primary)]">{n.name}</b>
          {n.synthesised ? (
            <span
              title="Proof-of-concept stand-in — not in the site directory"
              className="rounded border border-dashed border-[var(--hairline-strong)] px-1 text-[9.5px] uppercase tracking-[0.05em] text-[var(--ink-tertiary)]"
            >
              stand-in
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 text-[11px] text-[var(--ink-tertiary)]">
          {row.mineName} · crew {n.crew} · {row.classLabel}
        </div>
        <div className="mono mt-0.5 text-[10.5px] text-[var(--ink-tertiary)]">
          {n.hours} h · {n.events} events · {row.pLabel}
        </div>
      </div>

      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="mono text-[22px] font-semibold leading-none" style={{ color: 'var(--info)' }}>
            {row.ratioLabel}
          </span>
          <span className="text-[11px] text-[var(--ink-tertiary)]">site peers</span>
        </div>
        <div className="mt-2 space-y-1">
          <RateBar label="them" value={n.ratePer100h} pct={row.ratePct} color="var(--info)" />
          <RateBar label="peers" value={n.peerRate} pct={row.peerPct} color="var(--ink-tertiary)" />
        </div>
        <div className="mt-1 text-[10px] text-[var(--ink-tertiary)]">events per 100 operating hours</div>
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap gap-1">
          {row.codes.map((c) => (
            <span
              key={c.code}
              title={c.code}
              className="rounded bg-[var(--surface-2)] px-1.5 py-0.5 text-[10.5px] text-[var(--ink-secondary)]"
            >
              {c.label} <span className="mono text-[var(--ink-tertiary)]">×{c.count}</span>
            </span>
          ))}
        </div>
        <p className="mt-2 text-[11.5px] leading-relaxed text-[var(--ink-secondary)]">{n.recommendation}</p>
      </div>

      <div className="flex flex-col items-start gap-1 lg:items-end">
        <button
          type="button"
          onClick={onBook}
          disabled={booked}
          className={cn(
            'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-[11px] font-medium transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]',
            booked
              ? 'cursor-default bg-[var(--good-soft)] text-[var(--good)]'
              : 'bg-[var(--accent)] text-[var(--accent-text)] hover:opacity-90',
          )}
        >
          <GraduationCap aria-hidden className="h-3.5 w-3.5" />
          {booked ? 'Coaching booked' : 'Book coaching'}
        </button>
        <span className="text-[10.5px] text-[var(--ink-tertiary)]">with {row.supervisor}</span>
      </div>
    </article>
  );
}

function RateBar({ label, value, pct, color }: { label: string; value: number; pct: number; color: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-9 text-[10px] text-[var(--ink-tertiary)]">{label}</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--surface-3)]">
        <i className="block h-full rounded-full" style={{ width: `${Math.max(1.5, pct)}%`, background: color }} />
      </div>
      <span className="mono w-9 text-right text-[10.5px] text-[var(--ink-secondary)]">{value.toFixed(1)}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Peer strip chart
// ---------------------------------------------------------------------------

const STRIP_VIEW_W = 1000;
const STRIP_GUTTER = 170;
const STRIP_PAD_R = 24;
const STRIP_ROW_H = 34;
const STRIP_AXIS_H = 28;
const STRIP_PAD_T = 18;
const STRIP_TICKS = [0, 1, 2, 4, 6, 8];

function PeerStripChart({ strips }: { strips: readonly SiteStrip[] }) {
  const plotL = STRIP_GUTTER;
  const plotR = STRIP_VIEW_W - STRIP_PAD_R;
  const x = (v: number) => plotL + (v / STRIP_MAX_RATIO) * (plotR - plotL);
  const height = STRIP_PAD_T + strips.length * STRIP_ROW_H + STRIP_AXIS_H;
  const plotH = strips.length * STRIP_ROW_H;
  const threshold = 1.8;

  return (
    <svg
      viewBox={`0 0 ${STRIP_VIEW_W} ${height}`}
      className="w-full"
      style={{ height: 'auto', overflow: 'visible' }}
      role="img"
      aria-label={`Operators by site against their peers. ${strips
        .map((s) => `${s.mineName}: ${s.operators} operators, ${s.flagged} flagged`)
        .join('; ')}.`}
    >
      <g transform={`translate(0 ${STRIP_PAD_T})`}>
      {/* Tick gridlines */}
      {STRIP_TICKS.map((t) => (
        <line key={t} x1={x(t)} x2={x(t)} y1={0} y2={plotH} stroke="var(--hairline)" strokeWidth={1} />
      ))}
      {/* Peer parity and the flagging threshold */}
      <line x1={x(1)} x2={x(1)} y1={0} y2={plotH} stroke="var(--ink-tertiary)" strokeWidth={1.25} />
      <line
        x1={x(threshold)}
        x2={x(threshold)}
        y1={0}
        y2={plotH}
        stroke="var(--info)"
        strokeWidth={1.25}
        strokeDasharray="4 3"
      />
      <rect
        x={x(threshold)}
        y={0}
        width={plotR - x(threshold)}
        height={plotH}
        fill="var(--info-soft)"
        opacity={0.35}
      />

      {strips.map((s, i) => {
        const cy = i * STRIP_ROW_H + STRIP_ROW_H / 2;
        return (
          <g key={s.mine}>
            <line x1={plotL} x2={plotR} y1={cy} y2={cy} stroke="var(--hairline)" strokeWidth={1} />
            <text x={plotL - 12} y={cy - 2} textAnchor="end" fontSize={12} fill="var(--ink-primary)">
              {s.mineName}
            </text>
            <text x={plotL - 12} y={cy + 11} textAnchor="end" fontSize={10} fill="var(--ink-tertiary)">
              {s.operators} operators{s.flagged ? ` · ${s.flagged} flagged` : ''}
            </text>
            {s.points.map((p) => (
              <circle
                key={p.operatorId}
                cx={x(p.x)}
                cy={cy + p.jitter * (STRIP_ROW_H * 0.26)}
                r={p.flagged ? 5 : 3.2}
                fill={p.flagged ? 'var(--info)' : 'var(--ink-tertiary)'}
                fillOpacity={p.flagged ? 1 : 0.4}
                stroke={p.flagged ? 'var(--surface-1)' : 'none'}
                strokeWidth={p.flagged ? 1.5 : 0}
              >
                <title>{`${p.name} · ${p.ratio >= 99 ? 'peers clean' : `${p.ratio.toFixed(2)}× site peers`}${p.flagged ? ' · flagged' : ''}`}</title>
              </circle>
            ))}
            {s.points
              .filter((p) => p.labelled)
              .map((p) => {
                const nearEdge = x(p.x) > plotR - 110;
                return (
                  <text
                    key={`label-${p.operatorId}`}
                    x={nearEdge ? x(p.x) - 9 : x(p.x) + 9}
                    y={cy + p.jitter * (STRIP_ROW_H * 0.26) + 4}
                    textAnchor={nearEdge ? 'end' : 'start'}
                    fontSize={10.5}
                    fontWeight={600}
                    fill="var(--info)"
                    stroke="var(--surface-1)"
                    strokeWidth={3}
                    paintOrder="stroke"
                  >
                    {p.name}
                  </text>
                );
              })}
          </g>
        );
      })}

      {/* Axis */}
      {STRIP_TICKS.map((t) => (
        <text
          key={t}
          x={x(t)}
          y={plotH + 14}
          textAnchor="middle"
          fontSize={10}
          fill="var(--ink-tertiary)"
          className="mono"
        >
          {t === STRIP_MAX_RATIO ? `${t}×+` : `${t}×`}
        </text>
      ))}
      </g>
      <text x={x(1)} y={11} textAnchor="middle" fontSize={9.5} fill="var(--ink-tertiary)">
        site average
      </text>
      <text x={x(threshold) + 4} y={11} fontSize={9.5} fill="var(--info)">
        1.8× flag line
      </text>
    </svg>
  );
}
